// ✅ BillListing.jsx (REDESIGNED — EDIT/SELECT COLUMNS + RESULTS SUMMARY)

import React, { useState, useContext, useEffect, useMemo } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
} from "react-icons/fa";

const BASE_URL =
  import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

// ✅ Helpers
const isValidDDMMYYYY = (value) => {
  if (!value) return true;
  const re = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const m = value.match(re);
  if (!m) return false;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);

  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  if (yyyy < 1900 || yyyy > 3000) return false;

  return true;
};

const toFixed2 = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2);
};

const formatAmount = (v) => `₹${toFixed2(v)}`;

const BillListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchTypeLS,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [searchData, setSearchData] = useState({
    fromDate: "13/08/2025",
    toDate: "12/09/2025",
    billNo: "",
  });

  const [billData, setBillData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Row selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // ✅ Pagination (client side)
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // ✅ Clear filters — dates reset to empty, then reload full list
  const handleClearFilters = () => {
    setSearchData({
      fromDate: "",
      toDate: "",
      billNo: "",
    });
    setTimeout(() => fetchViewAll(), 0);
  };

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(billData.length / pageSize));
  }, [billData.length]);

  const pagedBills = useMemo(() => {
    const start = (page - 1) * pageSize;
    return billData.slice(start, start + pageSize);
  }, [billData, page]);

  // ✅ IDs in current page (for "select all" in current page)
  const pageIds = useMemo(() => {
    return pagedBills
      .map((b) => b.fld_billid)
      .filter((id) => id !== undefined && id !== null);
  }, [pagedBills]);

  const allCheckedOnPage = useMemo(() => {
    if (pageIds.length === 0) return false;
    return pageIds.every((id) => selectedIds.includes(id));
  }, [pageIds, selectedIds]);

  const someCheckedOnPage = useMemo(() => {
    if (pageIds.length === 0) return false;
    return pageIds.some((id) => selectedIds.includes(id));
  }, [pageIds, selectedIds]);

  // ✅ Totals for the Results Summary panel
  const totalAmount = useMemo(
    () => billData.reduce((sum, b) => sum + (Number(b.fld_billamount) || 0), 0),
    [billData]
  );

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const fetchViewAll = async () => {
    try {
      if (!branchIdLS) {
        toast.error("branchId not found in localStorage");
        return;
      }

      setLoading(true);
      setPage(1);
      setSelectedIds([]);

      const res = await axios.get(`${BASE_URL}/api/bills/viewall`, {
        params: { branchid: branchIdLS },
        headers: commonHeaders,
      });

      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setBillData(rows);
      toast.success(`Loaded ${rows.length} bills`);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load bills (View All)"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSearch = async () => {
    if (!branchIdLS) {
      toast.error("branchId not found in localStorage");
      return;
    }

   const formatDateToDDMMYYYY = (date) => {
  if (!date) return "";

  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
};

// From Date Validation
if (
  searchData.fromDate &&
  !isValidDDMMYYYY(formatDateToDDMMYYYY(searchData.fromDate))
) {
  toast.error("From Date must be in DD/MM/YYYY");
  return;
}

// To Date Validation
if (
  searchData.toDate &&
  !isValidDDMMYYYY(formatDateToDDMMYYYY(searchData.toDate))
) {
  toast.error("To Date must be in DD/MM/YYYY");
  return;
}

    try {
      setLoading(true);
      setPage(1);
      setSelectedIds([]);

      const res = await axios.get(`${BASE_URL}/api/bills/search`, {
        params: {
          branchid: branchIdLS,
          fromDate: searchData.fromDate?.trim() || "",
          toDate: searchData.toDate?.trim() || "",
          billNo: searchData.billNo?.trim() || "",
        },
        headers: commonHeaders,
      });

      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setBillData(rows);
      toast.info(`Found ${rows.length} bills`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to search bills");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate("/PaymentVouchers/LabourChargesBillGenerationEntry");
    toast.success("Add new bill form opened!");
  };

  // ✅ Toggle single row selection
  const toggleRow = (billId) => {
    setSelectedIds((prev) => {
      if (prev.includes(billId)) return prev.filter((x) => x !== billId);
      return [...prev, billId];
    });
  };

  // ✅ Select all rows in current page
  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      if (allCheckedOnPage) {
        return prev.filter((id) => !pageIds.includes(id));
      }
      const set = new Set(prev);
      pageIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  // ✅ DELETE selected rows
  const handleDeleteSelected = async () => {
    if (!branchIdLS) {
      toast.error("branchId not found in localStorage");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select at least one bill to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} bill(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((billid) =>
          axios.delete(`${BASE_URL}/api/labourbill/delete`, {
            params: { branchid: branchIdLS, billid },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted successfully ✅");
      setBillData((prev) =>
        prev.filter((b) => !selectedIds.includes(b.fld_billid))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to delete selected bills"
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATE — same navigation logic as before, now triggered per-row via the Edit icon
  const handleUpdateRow = (billid) => {
    if (!billid) {
      toast.warn("Invalid bill row");
      return;
    }

    navigate(`/PaymentVouchers/LabourChargesBillGenerationEntry?billid=${billid}`);
  };

  useEffect(() => {
    fetchViewAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Listing
              </h1>
              <span
                className={`text-sm px-3 py-1 rounded-full border ${
                  isDark ? "border-white/20 text-white/90" : "border-gray-200 text-gray-700"
                }`}
              >
                Branch: {branchIdLS || "-"}
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                    <button
                  onClick={handleClearFilters}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                    loading
                      ? "opacity-60 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  Clear
                </button>
                  <button
                    onClick={fetchViewAll}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      loading
                        ? "opacity-60 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaEye />
                    {loading ? "Loading..." : "View All"}
                  </button>

                  <button
                    onClick={fetchSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleSearchChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleSearchChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill No
                  </label>
                  <input
                    type="text"
                    value={searchData.billNo}
                    onChange={(e) => handleSearchChange("billNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Enter bill number (prefix)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">BillNo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">BillDate</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Billed By</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL (current page) - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={allCheckedOnPage}
                      ref={(el) => {
                        if (el) el.indeterminate = !allCheckedOnPage && someCheckedOnPage;
                      }}
                      onChange={toggleSelectAllPage}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All (this page)"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : pagedBills.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No bills found.
                    </td>
                  </tr>
                ) : (
                  pagedBills.map((bill, idx) => {
                    const id = bill.fld_billid;
                    const checked = selectedIds.includes(id);

                    return (
                      <tr
                        key={id ?? idx}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {bill.fld_billno}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {bill.fld_billdate}
                        </td>
                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {formatAmount(bill.fld_billamount)}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {bill.fld_billedby}
                        </td>

                        {/* EDIT */}
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(id)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        {/* SELECT */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE + PAGINATION ==================== */}
            <div
              className={`flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border-t ${
                isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-800"
              }`}
            >
              <button
                onClick={handleDeleteSelected}
                disabled={loading || selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  loading || selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                 🗑
                Remove
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className={`px-3 py-1 rounded border ${
                    page === 1 ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                  } ${isDark ? "border-gray-700" : "border-gray-300"}`}
                >
                  {"<<"}
                </button>

                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 rounded border ${
                    page === 1 ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                  } ${isDark ? "border-gray-700" : "border-gray-300"}`}
                >
                  Prev
                </button>

                <span className="text-sm">
                  Page <b>{page}</b> / <b>{totalPages}</b>
                </span>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-3 py-1 rounded border ${
                    page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                  } ${isDark ? "border-gray-700" : "border-gray-300"}`}
                >
                  Next
                </button>

                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className={`px-3 py-1 rounded border ${
                    page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                  } ${isDark ? "border-gray-700" : "border-gray-300"}`}
                >
                  {">>"}
                </button>
              </div>
            </div>
          </div>

       

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default BillListing;